import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";
import { dailyLogKeys } from "@/lib/query";

export interface DailyLog {
  id: string;
  projectId: string;
  date: string | null;
  weatherCondition: string | null;
  weatherTempHigh: number | null;
  weatherTempLow: number | null;
  notes: string | null;
  createdBy: string | null;
  status: "draft" | "submitted" | "signed_off" | "void";
  submittedAt: string | null;
  submittedByName: string | null;
  hasWeatherDelay: boolean | null;
  manpowerCount: number | null;
  deliveryCount: number | null;
}

export interface ManpowerEntry {
  id: string;
  dailyLogId: string;
  companyId: string;
  companyName: string;
  trade: string;
  workerCount: number;
  hoursWorked: number;
  notes: string | null;
}

export interface MaterialDelivery {
  id: string;
  dailyLogId: string;
  companyId: string | null;
  materialDescription: string;
  quantity: string;
  deliveryTime: string | null;
  receivedBy: string | null;
  notes: string | null;
}

export interface DelayEntry {
  id: string;
  dailyLogId: string;
  delayType: string;
  description: string;
  hoursLost: number | null;
  isCompensable: boolean;
}

export interface DailyLogDetail extends DailyLog {
  manpowerEntries: ManpowerEntry[];
  deliveries: MaterialDelivery[];
  delays: DelayEntry[];
}

export interface DailyLogFilters {
  projectId?: string;
  dateFrom?: string;
  dateTo?: string;
  hasWeatherDelay?: boolean;
}

function buildDailyLogQuery(filters?: DailyLogFilters): string {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set("projectId", filters.projectId);
  if (filters?.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters?.dateTo) params.set("dateTo", filters.dateTo);
  if (filters?.hasWeatherDelay !== undefined) {
    params.set("hasWeatherDelay", String(filters.hasWeatherDelay));
  }
  const qs = params.toString();
  return qs ? `/dailylogs?${qs}` : "/dailylogs";
}

export function useDailyLogs(filters?: DailyLogFilters) {
  return useQuery({
    queryKey: [...dailyLogKeys.all, filters ?? null],
    queryFn: () => apiFetch<DailyLog[]>(buildDailyLogQuery(filters)),
  });
}

export function useDailyLog(id: string, enabled = true) {
  return useQuery({
    queryKey: dailyLogKeys.detail(id),
    queryFn: () => apiFetch<DailyLogDetail>(`/dailylogs/${id}`),
    enabled: enabled && id.length > 0,
  });
}

interface CreateDailyLogInput {
  projectId: string;
  date: string;
  backdateReason?: string;
}

export function useCreateDailyLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDailyLogInput) => apiPost<DailyLogDetail>("/dailylogs", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dailyLogKeys.all });
      toast.success("Daily log created");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to create daily log");
    },
  });
}

export function useSubmitDailyLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<DailyLog>(`/dailylogs/${id}/submit`),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: dailyLogKeys.all });
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(data.id) });
      toast.success("Daily log submitted");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to submit daily log");
    },
  });
}

export interface PatchDailyLogInput {
  notes?: string | null;
  weatherCondition?: string | null;
  weatherTempHigh?: number | null;
  weatherTempLow?: number | null;
}

export function usePatchDailyLog(dailyLogId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchDailyLogInput) =>
      apiPatch<DailyLogDetail>(`/dailylogs/${dailyLogId}`, body),
    onSuccess: (data) => {
      qc.setQueryData(dailyLogKeys.detail(data.id), data);
      void qc.invalidateQueries({ queryKey: dailyLogKeys.all });
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(data.id) });
      toast.success("Daily log saved");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to save daily log");
    },
  });
}

export interface AddDelayEntryInput {
  delayType: string;
  description: string;
  hoursLost: number;
  responsibleCompanyId?: string;
  isCompensable?: boolean;
  notes?: string;
}

export function useAddDelayEntry(dailyLogId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddDelayEntryInput) =>
      apiPost<DelayEntry>(`/dailylogs/${dailyLogId}/delays`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(dailyLogId) });
      toast.success("Delay recorded");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to add delay");
    },
  });
}

export interface PatchDelayEntryInput {
  delayType?: string;
  description?: string;
  hoursLost?: number;
  responsibleCompanyId?: string | null;
  isCompensable?: boolean;
  notes?: string | null;
}

export function usePatchDelayEntry(dailyLogId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, ...body }: { entryId: string } & PatchDelayEntryInput) =>
      apiPatch<DelayEntry>(`/dailylogs/${dailyLogId}/delays/${entryId}`, body),
    onSuccess: (updated) => {
      qc.setQueryData<DailyLogDetail>(dailyLogKeys.detail(dailyLogId), (prev) => {
        if (!prev) return prev;
        const delays = prev.delays.map((row) =>
          row.id === updated.id ? { ...row, ...updated } : row,
        );
        const hasWeatherDelay = delays.some((d) => d.delayType === "weather");
        return { ...prev, delays, hasWeatherDelay };
      });
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(dailyLogId) });
      toast.success("Delay updated");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update delay");
    },
  });
}

export function useDeleteDelayEntry(dailyLogId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => apiDelete(`/dailylogs/${dailyLogId}/delays/${entryId}`),
    onSuccess: (_, entryId) => {
      qc.setQueryData<DailyLogDetail>(dailyLogKeys.detail(dailyLogId), (prev) => {
        if (!prev) return prev;
        const delays = prev.delays.filter((row) => row.id !== entryId);
        const hasWeatherDelay = delays.some((d) => d.delayType === "weather");
        return { ...prev, delays, hasWeatherDelay };
      });
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(dailyLogId) });
      toast.success("Delay removed");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to remove delay");
    },
  });
}

interface AddManpowerInput {
  companyId: string;
  trade: string;
  workerCount: number;
  hoursWorked: number;
  notes?: string;
}

export function useAddManpowerEntry(dailyLogId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddManpowerInput) =>
      apiPost<ManpowerEntry>(`/dailylogs/${dailyLogId}/manpower`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(dailyLogId) });
      toast.success("Manpower entry added");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to add manpower entry");
    },
  });
}

export interface PatchManpowerEntryInput {
  companyId?: string;
  trade?: string;
  workerCount?: number;
  hoursWorked?: number;
  notes?: string | null;
}

export function usePatchManpowerEntry(dailyLogId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, ...body }: { entryId: string } & PatchManpowerEntryInput) =>
      apiPatch<ManpowerEntry>(`/dailylogs/${dailyLogId}/manpower/${entryId}`, body),
    onSuccess: (updated) => {
      qc.setQueryData<DailyLogDetail>(dailyLogKeys.detail(dailyLogId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          manpowerEntries: prev.manpowerEntries.map((row) =>
            row.id === updated.id ? { ...row, ...updated } : row,
          ),
        };
      });
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(dailyLogId) });
      toast.success("Manpower entry updated");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update manpower entry");
    },
  });
}

export function useDeleteManpowerEntry(dailyLogId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => apiDelete(`/dailylogs/${dailyLogId}/manpower/${entryId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(dailyLogId) });
      toast.success("Manpower entry removed");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to remove manpower entry");
    },
  });
}

interface AddDeliveryInput {
  companyId?: string;
  materialDescription: string;
  quantity: string;
  deliveryTime?: string;
  receivedBy?: string;
  notes?: string;
}

export function useAddDelivery(dailyLogId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddDeliveryInput) =>
      apiPost<MaterialDelivery>(`/dailylogs/${dailyLogId}/deliveries`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(dailyLogId) });
      toast.success("Delivery logged");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to log delivery");
    },
  });
}

export interface PatchDeliveryInput {
  companyId?: string | null;
  materialDescription?: string;
  quantity?: string;
  deliveryTime?: string | null;
  receivedBy?: string | null;
  notes?: string | null;
}

export function usePatchDelivery(dailyLogId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, ...body }: { entryId: string } & PatchDeliveryInput) =>
      apiPatch<MaterialDelivery>(`/dailylogs/${dailyLogId}/deliveries/${entryId}`, body),
    onSuccess: (updated) => {
      qc.setQueryData<DailyLogDetail>(dailyLogKeys.detail(dailyLogId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          deliveries: prev.deliveries.map((row) =>
            row.id === updated.id ? { ...row, ...updated } : row,
          ),
        };
      });
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(dailyLogId) });
      toast.success("Delivery updated");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update delivery");
    },
  });
}

export function useDeleteDelivery(dailyLogId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => apiDelete(`/dailylogs/${dailyLogId}/deliveries/${entryId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dailyLogKeys.detail(dailyLogId) });
      toast.success("Delivery removed");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to remove delivery");
    },
  });
}
